import React, { useState, useRef, useEffect } from 'react';
import { Project } from '../types';
import { Youtube, Trophy, ExternalLink, Loader2, Play, Flame } from 'lucide-react';

interface TopProjectsSlideshowProps {
  projects: Project[];
  onSelect: (project: Project) => void;
}

// Helper to format large numbers (e.g., 125000 -> 125K)
const formatViews = (views: number) => {
  if (views >= 1000000) return (views / 1000000).toFixed(1) + 'M';
  if (views >= 1000) return (views / 1000).toFixed(1) + 'K';
  return views.toString();
};

interface TopProjectCardProps {
  project: Project;
  rank: number;
  onClick: () => void;
}

const TopProjectCard: React.FC<TopProjectCardProps> = ({ project, rank, onClick }) => {
  const [loaded, setLoaded] = useState(false);
  
  // Use stored thumbnail
  const previewUrl = project.thumbnailUrl;

  // Movie Poster Rank Colors (Metallic/Neon vibes)
  const getRankStyle = (r: number) => {
    if (r === 1) return 'text-yellow-400 drop-shadow-[0_0_15px_rgba(250,204,21,0.6)]';
    if (r === 2) return 'text-slate-300 drop-shadow-[0_0_10px_rgba(203,213,225,0.5)]';
    if (r === 3) return 'text-orange-400 drop-shadow-[0_0_10px_rgba(251,146,60,0.5)]';
    return 'text-white/20';
  };

  return (
    <div 
      onClick={onClick}
      className="relative flex-none w-[180px] sm:w-[220px] h-[320px] sm:h-[400px] rounded-xl overflow-hidden cursor-pointer group ring-1 ring-white/5 hover:ring-primary/40 transition-all hover:scale-105 hover:-translate-y-2 shadow-2xl bg-surface"
    >
      {/* Background Image (Mobile/Vertical View) */}
      <div className="absolute inset-0 bg-surface">
         {!loaded && (
           <div className="absolute inset-0 flex items-center justify-center bg-surfaceLight/50">
             <Loader2 size={24} className="text-slate-600 animate-spin" />
           </div>
         )}
         <img 
            src={previewUrl} 
            alt={project.title}
            className={`w-full h-full object-cover object-top transition-all duration-700 group-hover:scale-110 ${loaded ? 'opacity-60 group-hover:opacity-100' : 'opacity-0'}`}
            onLoad={() => setLoaded(true)}
            loading="lazy"
            onError={(e) => { (e.target as HTMLImageElement).src = 'https://picsum.photos/350/500?grayscale'; setLoaded(true); }}
         />
         {/* Cinematic Gradient Overlay */}
         <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-90 group-hover:opacity-80 transition-opacity" />
         
         {/* Top Gradient for text readability */}
         <div className="absolute top-0 inset-x-0 h-24 bg-gradient-to-b from-black/80 to-transparent" />
      </div>

      {/* Giant Rank Number (Behind content but visible) */}
      <div className={`absolute top-[-10px] left-[-10px] text-[120px] font-black leading-none select-none z-10 opacity-90 font-sans tracking-tighter ${getRankStyle(rank)}`}>
        {rank}
      </div>

      {/* Content Layer */}
      <div className="absolute inset-0 p-4 flex flex-col justify-end z-20">
        <div className="transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
           
           {/* Tags */}
           <div className="flex gap-1 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-100">
              <span className="text-[9px] px-1.5 py-0.5 bg-white/20 backdrop-blur-md rounded text-white font-medium">
                Day {project.id}
              </span>
              <span className="text-[9px] px-1.5 py-0.5 bg-primary/80 backdrop-blur-md rounded text-white font-medium">
                {project.category}
              </span>
           </div>

           {/* Title */}
           <h3 className="text-xl font-bold text-white mb-1.5 leading-tight line-clamp-2 drop-shadow-lg">
             {project.title}
           </h3>
           
           {/* Stats */}
           <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-red-400">
                 <Youtube size={14} fill="currentColor" />
                 <span className="text-xs font-bold tracking-wide text-red-100">{formatViews(project.views || 0)}</span>
              </div>
              
              {/* Hot Indicator */}
              {(project.views || 0) > 50000 && (
                <div className="flex items-center gap-1 text-orange-400">
                  <Flame size={12} fill="currentColor" />
                  <span className="text-[9px] font-bold uppercase">Viral</span>
                </div>
              )}
           </div>
        </div>
      </div>
      
      {/* Hover Action Overlay */}
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/10 backdrop-blur-[2px] z-30 pointer-events-none">
         <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-xl border border-white/40 text-white shadow-[0_0_30px_rgba(255,255,255,0.3)] flex items-center justify-center">
            <Play size={24} fill="currentColor" className="ml-1" />
         </div>
      </div>
    </div>
  );
};

export const TopProjectsSlideshow: React.FC<TopProjectsSlideshowProps> = ({ projects, onSelect }) => {
  const [isPaused, setIsPaused] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isReady, setIsReady] = useState(false);

  // Sort by views descending and take top 10
  const topProjects = [...projects]
    .filter(p => p.views !== undefined)
    .sort((a, b) => (b.views || 0) - (a.views || 0))
    .slice(0, 10);

  // Duplicate to allow infinite scrolling effect
  // We use 3 sets: [Prev Set] [Current Set] [Next Set]
  // This ensures that whether we scroll left or right, there is always content.
  const displayProjects = [...topProjects, ...topProjects, ...topProjects];

  // Initialize scroll position to the middle set
  useEffect(() => {
    const container = scrollRef.current;
    if (container && topProjects.length > 0) {
      // Use setTimeout to ensure the DOM layout is fully painted
      const timer = setTimeout(() => {
        const singleSetWidth = container.scrollWidth / 3;
        container.scrollLeft = singleSetWidth;
        setIsReady(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [topProjects.length]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container || !isReady) return;

    let animationId: number;

    const animate = () => {
      if (container) {
         const singleSetWidth = container.scrollWidth / 3;
         
         // Auto-scroll logic (Slow speed: 0.4 pixels per frame)
         if (!isPaused) {
             container.scrollLeft += 0.4;
         }

         // Bidirectional Infinite Loop Logic
         // 1. Moving Right: If we reach the start of Set 3, snap back to Set 2
         if (container.scrollLeft >= singleSetWidth * 2) {
             container.scrollLeft = singleSetWidth; 
         }
         // 2. Moving Left: If we reach the start of Set 1 (0), snap forward to Set 2
         else if (container.scrollLeft <= 0) {
             container.scrollLeft = singleSetWidth;
         }
      }
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationId);
  }, [isPaused, isReady, topProjects.length]);

  if (topProjects.length === 0) return null;

  return (
    <div className="w-full mb-12 border-b border-white/5 bg-black/20 pb-8 pt-4 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-6 mb-6 flex items-end justify-between">
         <div>
            <div className="flex items-center gap-2 mb-1">
                <span className="px-2 py-0.5 bg-red-600 text-white text-[10px] font-bold uppercase tracking-wider rounded">Trending Now</span>
            </div>
            <h2 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2">
               Most Viewed on YouTube
            </h2>
         </div>
         <div className="text-right hidden sm:block">
            <p className="text-xs text-textMuted">Data based on actual channel analytics</p>
         </div>
      </div>

      {/* Infinite Scroll Container */}
      <div className={`relative w-full group/track transition-opacity duration-500 ${isReady ? 'opacity-100' : 'opacity-0'}`}>
        
        {/* The Track */}
        <div 
            ref={scrollRef}
            className="flex gap-6 overflow-x-auto pb-4 px-4 scrollbar-hide"
            style={{ 
                scrollbarWidth: 'none', 
                msOverflowStyle: 'none',
                cursor: 'grab'
            }}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onTouchStart={() => setIsPaused(true)}
            onTouchEnd={() => setTimeout(() => setIsPaused(false), 2000)}
        >
          
          {displayProjects.map((project, index) => (
            <TopProjectCard 
              key={`${project.id}-${index}`} 
              project={project} 
              // Calculate rank based on original list
              rank={(index % topProjects.length) + 1} 
              onClick={() => onSelect(project)} 
            />
          ))}
          
        </div>

        {/* Fade Edges */}
        <div className="absolute top-0 left-0 w-12 sm:w-24 h-full bg-gradient-to-r from-background to-transparent z-40 pointer-events-none" />
        <div className="absolute top-0 right-0 w-12 sm:w-24 h-full bg-gradient-to-l from-background to-transparent z-40 pointer-events-none" />
      </div>
    </div>
  );
};